import { useEffect, useRef, useState } from "react";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import "../../css/EmblaHero.css";
import {
  Box,
  CardMedia,
  Fade,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  NextHeroButton,
  PrevHeroButton,
  usePrevNextHeroButtons,
} from "./components/EmblaHeroButton";
import { heroCarrusel } from "../../helpers/HeroCarrusel";
import { Link } from "react-router-dom";
import Autoplay from "embla-carousel-autoplay";
import { DotButton, useDotButton } from "./components/useDotButton";
import { Pause, Play } from "../../icons";

type PropType = {
  options?: EmblaOptionsType;
};

export const EmblaHero: React.FC<PropType> = ({ options }) => {
  const [play, setPlay] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    Autoplay({
      delay: 8000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      active: play,
    }),
  ]);
  const videoRef = useRef<HTMLVideoElement>(null);

  const onPlay = () => {
    setPlay((prev) => !prev);
  };

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextHeroButtons(emblaApi);
  const theme = useTheme();

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = async () => {
      const selectedIndex = emblaApi.selectedScrollSnap();

      const slides = emblaApi.slideNodes();
      slides.forEach((slide, index) => {
        const textBox = slide.querySelector(".texto");
        if (textBox) {
          if (index === selectedIndex) {
            textBox.classList.remove("animate");
            textBox.classList.add("animate");
          } else {
            textBox.classList.remove("animate");
          }
        }
      });

      if (selectedIndex === 0 && videoRef.current) {
        try {
          if (videoRef.current.paused) {
            videoRef.current.currentTime = 0;
            await videoRef.current.play();
          }
        } catch (err) {
          console.warn("No se pudo reproducir el video:", err);
        }
      } else if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section className="embla__hero">
      <Fade in timeout={1500}>
        <Box className="embla__viewport__hero" ref={emblaRef}>
          <Box className="embla__container__hero">
            {heroCarrusel.map((v) => (
              <Box className="embla__slide__hero" key={v.url}>
                {v.type === "video" ? (
                  <video
                    ref={videoRef}
                    muted
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                    }}
                    autoPlay
                    playsInline
                    loop
                    poster={v.poster}
                    src={v.url}
                  />
                ) : (
                  <CardMedia
                    component="img"
                    image={v.url}
                    width="100%"
                    height="100%"
                    alt="carrusel"
                  />
                )}

                <Box
                  sx={{ userSelect: "none" }}
                  className="texto"
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  position="absolute"
                  left={{ xs: 10, md: 50 }}
                  bottom={{ xs: 35, md: 70 }}
                  zIndex={9999}
                >
                  <Typography
                    variant="h1"
                    zIndex={99}
                    sx={{
                      color: theme.palette.background.default,
                      fontSize: { xs: 25, sm: 35, md: 50 },
                    }}
                  >
                    {v.title}
                  </Typography>
                  <Typography
                    zIndex={99}
                    maxWidth={{ xs: 240, md: 500 }}
                    variant="body1"
                    sx={{
                      position: "relative",
                      left: 2,
                      color: theme.palette.background.paper,
                      fontSize: { xs: 12, md: 15 },
                    }}
                  >
                    {v.subtitle}
                  </Typography>
                  {v.to !== "" && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      mt={2}
                      zIndex={99}
                    >
                      <Link
                        to={v.to}
                        target={v.to.includes("https") ? "_blank" : ""}
                        className="btn-hero"
                      >
                        {v.label}
                      </Link>
                    </Box>
                  )}
                  <Box
                    width="100%"
                    zIndex={5}
                    position="absolute"
                    bottom={50}
                    boxShadow={{
                      xs: "0px 0px 100px 25px black",
                      md: "0px 0px 200px 50px black",
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>

          <Box className="embla__controls__hero">
            <Box display="flex" gap={2}>
              <IconButton
                onClick={onPlay}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: 35,
                  height: 35,
                  backdropFilter: "saturate(180%) blur(5px)",
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: "100vmax !important",
                  "&:hover": {
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              >
                {!play ? (
                  <Play
                    sx={{
                      width: { xs: 15, md: 19 },
                      height: { xs: 15, md: 19 },
                    }}
                  />
                ) : (
                  <Pause
                    sx={{
                      width: { xs: 15, md: 19 },
                      height: { xs: 15, md: 19 },
                    }}
                  />
                )}
              </IconButton>

              <PrevHeroButton
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
              />
              <NextHeroButton
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
              />
            </Box>
          </Box>
        </Box>
      </Fade>
      <Box className="embla__dots">
        {scrollSnaps.map((_, index) => (
          <DotButton
            key={index}
            onClick={() => onDotButtonClick(index)}
            className={"embla__dot".concat(
              index === selectedIndex ? " embla__dot--selected" : "",
            )}
          />
        ))}
      </Box>
    </section>
  );
};
